import React, { useState } from 'react';
import { fetchInvoicesCsv } from './api';

export const NO_FILTERS = { q: '', from: '', to: '' };

/** Whether any filter is set. */
export const isFiltered = (f) => !!(f.q.trim() || f.from || f.to);

/**
 * The rows that match the filters: the text in the supplier's name or the invoice number (plain text, any case) and the
 * invoice date from/to, both days included. Mirrors the server (backend/invoice_list.py) so the file you export holds the
 * rows you are looking at. A row with no readable date is left out once a date filter is set.
 */
export function applyFilters(rows, f) {
  const needle = f.q.trim().toLowerCase();
  return (rows || []).filter((r) => {
    const day = String(r.date || '').slice(0, 10);
    if (f.from && !(day && day >= f.from)) return false;
    if (f.to && !(day && day <= f.to)) return false;
    if (needle && !String(r.supplier_name || '').toLowerCase().includes(needle)
        && !String(r.invoice_no || '').toLowerCase().includes(needle)) return false;
    return true;
  });
}

/** The query the export sends for the tab being looked at ("posted" is auto + approved, so no state). */
export function exportQuery(tab, f) {
  const params = new URLSearchParams();
  if (tab && tab.states.length === 1) params.set('state', tab.states[0]);
  if (f.q.trim()) params.set('q', f.q.trim());
  if (f.from) params.set('date_from', f.from);
  if (f.to) params.set('date_to', f.to);
  return params.toString();
}

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const FIELD = 'min-h-[44px] rounded-lg border border-surface-border bg-surface px-3 text-ink';

/** Search and date range over the list being looked at, and a spreadsheet of what matches (owners, admins, managers, viewers). */
export default function InvoiceFilters({ filters, onChange, tab, shown, canExport = true }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (key) => (e) => onChange({ ...filters, [key]: e.target.value });
  const exportCsv = async () => {
    setBusy(true); setError(null);
    try { saveBlob(await fetchInvoicesCsv(exportQuery(tab, filters)), 'invoices.csv'); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3" data-testid="invoice-filters">
      <label className="flex-1 min-w-[12rem]">
        <span className="block text-xs uppercase tracking-overline text-ink-muted mb-1">Supplier or invoice number</span>
        <input type="search" value={filters.q} onChange={set('q')} data-testid="invoice-filter-q" className={`${FIELD} w-full`} />
      </label>
      <label>
        <span className="block text-xs uppercase tracking-overline text-ink-muted mb-1">Invoice date from</span>
        <input type="date" value={filters.from} onChange={set('from')} data-testid="invoice-filter-from" className={FIELD} />
      </label>
      <label>
        <span className="block text-xs uppercase tracking-overline text-ink-muted mb-1">to</span>
        <input type="date" value={filters.to} onChange={set('to')} data-testid="invoice-filter-to" className={FIELD} />
      </label>
      {isFiltered(filters) && (
        <button type="button" onClick={() => onChange(NO_FILTERS)} data-testid="invoice-filter-clear"
          className="min-h-[44px] px-4 rounded-xl border border-surface-border text-ink hover:bg-surface-sunken">Clear</button>
      )}
      {canExport && (
        <button type="button" disabled={busy} onClick={exportCsv} data-testid="invoice-export"
          className="min-h-[44px] px-4 rounded-xl border border-surface-border text-ink hover:bg-surface-sunken">
          {busy ? 'Preparing…' : `Export ${shown} to a spreadsheet`}
        </button>
      )}
      {error && <p role="alert" className="w-full text-sm text-danger" data-testid="invoice-export-error">{error}</p>}
    </div>
  );
}
